// A full Instagram Story.
const STORY_WIDTH = 1080;
const STORY_HEIGHT = 1920;
const STORY_CENTER_X = STORY_WIDTH / 2;

// Bundled with the app (public/fonts), so every browser draws the same letters.
const STORY_FONT_FAMILY = 'Neue Montreal';
const DISPLAY_FONT_FAMILY = 'Recoleta';
const FontFamily = {
  TEXT: `"${STORY_FONT_FAMILY}", sans-serif`,
  DISPLAY: `"${DISPLAY_FONT_FAMILY}", serif`,
  MONO: 'ui-monospace, Menlo, monospace',
};

const MIN_FONT_SIZE = 12;
const ELLIPSIS = '…';

const font = (weight, size, family = FontFamily.TEXT) => `${weight} ${size}px ${family}`;

// Traced by hand rather than with ctx.roundRect, which iOS 15 Safari lacks.
const roundedRectPath = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
};

const circlePath = (ctx, centerX, centerY, radius) => {
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  ctx.closePath();
};

/*
  Fills the box with the image, cropping the overflow, like CSS `object-fit: cover`. `focusY` is where the crop
  sits vertically, like `object-position`: 0.5 crops evenly, 0.2 keeps more of the top, where faces usually are.
*/
const drawCover = (ctx, image, x, y, width, height, focusY = 0.5) => {
  const scale = Math.max(width / image.width, height / image.height);
  const sourceWidth = width / scale;
  const sourceHeight = height / scale;
  ctx.drawImage(
    image,
    (image.width - sourceWidth) / 2,
    (image.height - sourceHeight) * focusY,
    sourceWidth,
    sourceHeight,
    x,
    y,
    width,
    height,
  );
};

/*
  Sets the largest font, up to `size`, that fits the text in `maxWidth`, and returns the text to draw: the text
  itself, or cut short with an ellipsis when even `minSize` is too wide.
*/
const setFittedFont = (ctx, text, {
  weight,
  size,
  maxWidth,
  family,
  minSize = MIN_FONT_SIZE,
}) => {
  let fitted = size;
  ctx.font = font(weight, fitted, family);
  while (fitted > minSize && ctx.measureText(text).width > maxWidth) {
    fitted = Math.max(minSize, fitted - 1);
    ctx.font = font(weight, fitted, family);
  }
  let cut = text;
  while (cut.length > 1 && ctx.measureText(cut).width > maxWidth) {
    cut = `${cut.slice(0, -2).trimEnd()}${ELLIPSIS}`;
  }
  return cut;
};

const measureSpacedText = (ctx, text, spacing) => (
  ctx.measureText(text).width + spacing * Math.max(0, text.length - 1)
);

// Draws the text with `spacing` between letters, like CSS `letter-spacing`, honoring ctx.textAlign.
const fillSpacedText = (ctx, text, x, y, spacing) => {
  const width = measureSpacedText(ctx, text, spacing);
  const { textAlign } = ctx;
  let left = x;
  if (textAlign === 'center') {
    left = x - width / 2;
  } else if (textAlign === 'right' || textAlign === 'end') {
    left = x - width;
  }
  ctx.textAlign = 'left';
  [...text].forEach((letter) => {
    ctx.fillText(letter, left, y);
    left += ctx.measureText(letter).width + spacing;
  });
  ctx.textAlign = textAlign;
};

/*
  Writes the text once around a whole circle, clockwise from its left point, letters upright on the outside, like
  an SVG textPath. The letters spread evenly, so any text closes the ring.
*/
const fillTextOnCircle = (ctx, text, { x, y, radius }) => {
  const letters = [...text];
  const widths = letters.map((letter) => ctx.measureText(letter).width);
  const spacing = (2 * Math.PI * radius - widths.reduce((total, width) => total + width, 0)) / letters.length;
  let angle = Math.PI;
  ctx.textAlign = 'center';
  letters.forEach((letter, index) => {
    const middle = angle + (widths[index] / 2) / radius;
    ctx.save();
    ctx.translate(x + radius * Math.cos(middle), y + radius * Math.sin(middle));
    ctx.rotate(middle + Math.PI / 2);
    ctx.fillText(letter, 0, 0);
    ctx.restore();
    angle += (widths[index] + spacing) / radius;
  });
};

// Runs `draw` rotated by `degrees` around the point, leaving the context as it found it.
const drawRotated = (ctx, { x, y, degrees }, draw) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.translate(-x, -y);
  draw();
  ctx.restore();
};

// Runs `draw` with a soft drop shadow, so a sticker lifts off the page.
const drawWithShadow = (ctx, draw, {
  color = 'rgba(0, 0, 0, 0.35)',
  blur = 48,
  offsetY = 18,
} = {}) => {
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetY = offsetY;
  draw();
  ctx.restore();
};

export {
  STORY_WIDTH,
  STORY_HEIGHT,
  STORY_CENTER_X,
  STORY_FONT_FAMILY,
  DISPLAY_FONT_FAMILY,
  FontFamily,
  font,
  roundedRectPath,
  circlePath,
  drawCover,
  setFittedFont,
  measureSpacedText,
  fillSpacedText,
  fillTextOnCircle,
  drawRotated,
  drawWithShadow,
};
